import { IconDownload } from '@tabler/icons-react'
import { useState } from 'react'

import { BetaLabel, ScalrBreadcrumb } from '@/components/customComponents'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DataTable } from '@/components/ui/data-table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import {
  selectAllBranches,
  selectRegionsError,
  selectRegionsLoading,
  useAppSelector,
} from '@/store'
import { KPIPayoutTableModel } from '@/types/targets'
import { kpiPayoutTableColumns } from '@/utils'

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4']

const YEARS = ['2021', '2022', '2023', '2024', '2025']

// Mock data based on kpiPayoutTableColumns model
const mockData: KPIPayoutTableModel[] = [
  {
    kpiId: '103',
    area: 'Supply Chain',
    kpiName: 'COGS %',
    description: 'Cogs to be at 31%',
    calculation: '[Total Sales] / [Previous Sales]',
    target: 31,
    bonus: 7,
    result: 34.4,
    payout: 0,
    achievement: [10, 50, 100],
  },
  {
    kpiId: '104',
    area: 'Ops',
    kpiName: 'Speed of Service',
    description: 'Goal to decrease Speed of Service to < 1.05',
    calculation:
      '[Total DT Service Time per Car Recorded from All Visits] / [Number of Visits]',
    target: 1.05,
    bonus: 6,
    result: 1.13,
    payout: 0,
    achievement: [10, 50, 100],
  },
  {
    kpiId: '105',
    area: 'Sales',
    kpiName: 'ADNS',
    description: 'Goal to reach average daily net sales of $2,000 per day',
    calculation: '[Total Net Sales] / [Total Number of Days]',
    target: 2000,
    bonus: 12,
    result: 2050,
    payout: 13.56,
    achievement: [10, 50, 100],
  },
  {
    kpiId: '106',
    area: 'Ops',
    kpiName: 'Labor Ratio',
    description: 'Goal to keep labor ratio under 26%',
    calculation: '[Store Labor Cost] / [Total Net Sales]',
    target: 32,
    bonus: 8,
    result: 33.5,
    payout: 0,
    achievement: [10, 50, 100],
  },
  {
    kpiId: '102',
    area: 'Ops',
    kpiName: 'Guest Satisfaction',
    description: 'Goal to increase Google Reviews to 4.9/5',
    calculation: '[Total Review Scores] / [Number of Total Reviews]',
    target: 4.8,
    bonus: 0,
    result: 5,
    payout: 0,
    achievement: [10, 50, 100],
  },
  {
    kpiId: '107',
    area: 'Marketing',
    kpiName: 'Frequency',
    description: 'Average 7-day frequency > 3.2',
    calculation:
      '[Unique Member IDs] / [Total Member IDs with Users with 5+ Orders]',
    target: 3.2,
    bonus: 0,
    result: 5,
    payout: 0,
    achievement: [10, 50, 100],
  },
]

const showPayoutSummary = () => {
  return (
    <Card className="flex justify-center gap-32 p-4">
      <h2 className="text-md">TOTAL ACHIEVEMENT</h2>
      <h2 className="text-md">13.56%</h2>
    </Card>
  )
}

const PayoutSummary = () => (
  <div className="mt-8">
    <h2 className="text-xl font-semibold mb-2">Payout Summary</h2>
    <div className="mb-4 text-sm">
      Total Payout is calculated as follows = Hourly Salary Rate x Standardized
      Weekly 40 Hours x Total Achievement
    </div>
    <div className="flex flex-col gap-4 max-w-md">
      <div className="rounded-xl border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-2 divide-y divide-gray-200">
          <div className="flex items-center p-3 text-sm">Salary</div>
          <div className="flex items-center p-3 text-sm">$18.50</div>
          <div className="flex items-center p-3 text-sm">Hrs/Wk</div>
          <div className="flex items-center p-3 text-sm">40</div>
          <div className="flex items-center p-3 text-sm">Wks</div>
          <div className="flex items-center p-3 text-sm">6</div>
        </div>
      </div>
      <div className="rounded-xl border border-gray-200 font-semibold grid grid-cols-2">
        <div className="flex items-center p-3 text-sm">Payout:</div>
        <div className="flex items-center p-3 text-sm">$602</div>
      </div>
    </div>
  </div>
)

const KPIPayoutPage = () => {
  const isLoading = useAppSelector(selectRegionsLoading)
  const error = useAppSelector(selectRegionsError)
  const allBranches = useAppSelector(selectAllBranches)

  const [selectedYear, setSelectedYear] = useState(YEARS[YEARS.length - 1])
  const [selectedQuarter, setSelectedQuarter] = useState(QUARTERS[0])
  const [selectedBranch, setSelectedBranch] = useState(allBranches[0] || '')

  // Handle loading state
  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>KPI Payouts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            Loading regions data...
          </div>
        </CardContent>
      </Card>
    )
  }

  // Handle error state
  if (error) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>KPI Payouts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-red-500">
            Error loading regions: {error}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>
          <div className="justify-between items-center flex">
            <span>KPI Payout</span>
            <BetaLabel />
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScalrBreadcrumb
          breadcrumbItems={[{ label: 'Targets', href: '/targets' }]}
          title="KPI Payouts"
        />
        <CardContent className="grid grid-cols-2 gap-4 px-0 py-4">
          <div className="flex gap-4">
            <Select
              value={selectedYear}
              onValueChange={(value) => setSelectedYear(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Year" />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={selectedQuarter}
              onValueChange={(value) => setSelectedQuarter(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Quarter" />
              </SelectTrigger>
              <SelectContent>
                {QUARTERS.map((quarter) => (
                  <SelectItem key={quarter} value={quarter}>
                    {quarter}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={selectedBranch}
              onValueChange={(value) => setSelectedBranch(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Branch" />
              </SelectTrigger>
              <SelectContent>
                {allBranches.map((branch) => (
                  <SelectItem key={branch} value={branch}>
                    {branch}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select>
              <SelectTrigger>
                <SelectValue placeholder="Select An Employee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                <SelectItem value="selected">Selected Employee</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-4 justify-end">
            <Button variant="outline" className="h-10 w-10 p-0 bg-white">
              <IconDownload className="h-5 w-5" />
            </Button>
            <Button
              disabled={!selectedYear || !selectedQuarter || !selectedBranch}
            >
              Filter
            </Button>
          </div>
        </CardContent>

        {selectedBranch && selectedQuarter && selectedYear ? (
          <>
            <DataTable
              data={mockData}
              columns={kpiPayoutTableColumns}
              isCard={false}
              footer={showPayoutSummary()}
            />
            <PayoutSummary />
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Please select all filters to view KPI payout data
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default KPIPayoutPage
